import { CurrencyField } from '@aeppic/types';


export interface FormatCurrencyOptions {
  locale?: string,
}

function assertCurrencyPrecision(precision: number) {
  if (!Number.isInteger(precision) || precision < 0 || precision > 20) {
    throw new Error(`Invalid currency precision: ${precision}`)
  }
}

function splitBaseUnitAmount(amount: number, precision: number) {
  const negative = amount < 0
  const digits = String(Math.abs(amount)).padStart(precision + 1, '0')

  if (precision === 0) {
    return {
      negative,
      major: digits,
      minor: '',
    }
  }

  return {
    negative,
    major: digits.slice(0, -precision),
    minor: digits.slice(-precision),
  }
}

function formatMajorPart(major: string, locale?: string) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).formatToParts(Number(major)).map(part => part.value).join('')
}

function formatCurrencyFromBaseUnits({ currency, precision, amount }: CurrencyField, options: FormatCurrencyOptions = {}): string {
  assertCurrencyPrecision(precision)

  if (!Number.isSafeInteger(amount)) {
    throw new Error(`Currency amount must be a safe integer in base units: ${amount}`)
  }

  const { negative, major, minor } = splitBaseUnitAmount(amount, precision)
  const integer = formatMajorPart(major, options.locale)

  return new Intl.NumberFormat(options.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
  }).formatToParts(negative ? -1 : 1).map(part => {
    switch (part.type) {
      case 'integer':
        return integer
      case 'group':
        return ''
      case 'decimal':
        return precision > 0 ? part.value : ''
      case 'fraction':
        return minor
      default:
        return part.value
    }
  }).join('')
}

export function formatCurrency({ currency, precision, amount }: CurrencyField, options: FormatCurrencyOptions = {}): string {
  if (currency == null) {
    return ''
  }

  return formatCurrencyFromBaseUnits({ currency, precision, amount }, options)
}
